import React from 'react';

import { FormTitle } from '@components/admin/FormTitle';
import { Layout } from '@components/admin/Layout';
import { Alert } from '@components/Alert';
import { Button } from '@components/Button';
import { TextInput } from '@components/Forms/TextInput';
import { SpinnerModal } from '@components/SpinnerModal';
import { usePatchPassword } from '@hooks/mutation/usePatchPassword';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { FormError } from '@utils/errors';
import { createValidate, formStrings, requiredStringField } from '@utils/forms';
import { createStrings } from '@utils/local';
import { PageMeta } from '@utils/pages';
import clsx from 'clsx';
import { Form, Formik } from 'formik';
import * as t from 'io-ts';

const strings = createStrings({
  ja: {
    title: 'リセット　パスワード',
    oldPassword: '現在のパスワード',
    newPassword: '新しいパスワード',
    newPasswordConfirm: '新しいパスワード（再入力）',
    update: '更新',
    success: 'パスワードが更新されました。',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    flex: 1,
    padding: '1.5rem 2rem',
  },
  backToList: {
    ...common.middleText,
    ...common.fontWeight500,
    color: theme.palette.grey[700],
    marginLeft: 9,
    cursor: 'pointer',
  },
  formBodyContainer: {
    ...common.flexColumn,
    alignItems: 'center',
    paddingTop: '2rem',
    flexGrow: 1,
  },
  formBody: {
    ...common.flexColumn,
    alignItems: 'left',
  },
  label: {
    ...common.middleText,
    color: `${theme.palette.grey['800']} !important`,
  },
  input: {
    ...common.middleText,
  },
  textInput: {
    borderColor: theme.palette.grey[400],
    width: 300,
  },
  rowContainer: {
    ...common.flexCenter,
    width: '100%',
  },
}));

const schema = t.type({
  old_password: requiredStringField(formStrings.password),
  new_password1: requiredStringField(formStrings.password),
  new_password2: requiredStringField(formStrings.password),
});

const initialValues: t.TypeOf<typeof schema> = {
  old_password: '',
  new_password1: '',
  new_password2: '',
};

export const Password: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { mutateAsync, isLoading, isError, isSuccess, error, reset } = usePatchPassword();

  return (
    <Layout title={strings.title}>
      <SpinnerModal open={isLoading} />
      <div className={classes.container}>
        <FormTitle title={strings.title} />
        <Formik
          initialValues={initialValues}
          validate={createValidate(schema)}
          onSubmit={async (values) => {
            await mutateAsync(values);
          }}
        >
          <Form className={classes.formBodyContainer}>
            <Alert open={isError} severity="error" onClose={reset}>
              {error instanceof FormError && error.getDisplayMessage()}
            </Alert>

            <Alert open={isSuccess} severity="success" onClose={reset}>
              {strings.success}
            </Alert>
            <div className={clsx(classes.formBody)}>
              <TextInput
                label={strings.oldPassword}
                name="old_password"
                type="password"
                itemVariant="column"
                formClasses={{
                  label: classes.label,
                }}
                classes={{
                  textInput: clsx(classes.textInput),
                  input: classes.input,
                }}
                required
              />
              <br />
              <TextInput
                label={strings.newPassword}
                name="new_password1"
                type="password"
                itemVariant="column"
                formClasses={{
                  label: classes.label,
                }}
                classes={{
                  textInput: clsx(classes.textInput),
                  input: classes.input,
                }}
                required
              />
              <br />
              <TextInput
                label={strings.newPasswordConfirm}
                name="new_password2"
                type="password"
                itemVariant="column"
                formClasses={{
                  label: classes.label,
                }}
                classes={{
                  textInput: clsx(classes.textInput),
                  input: classes.input,
                }}
                required
              />
              <br />
              <br />
              <div className={classes.rowContainer}>
                <Button
                  text={strings.update!}
                  color="primary"
                  type="submit"
                  shape="round"
                  style={{ width: 64 }}
                />
              </div>
            </div>
          </Form>
        </Formik>
      </div>
    </Layout>
  );
};

export const meta: PageMeta = {
  title: strings.title,
  listed: false,
};
